import { decodeJwt } from '@package/jwt'
import { createSharedComposable } from '@vueuse/core'
import { hash } from 'ohash'

const RIALTIC_EMAIL_DOMAINS = ['@rialtic.io', '@rialtic.email']

type Session = { permissions: string[] }

export const useUserSession = createSharedComposable(() => {
  const { $auth0 } = useNuxtApp()
  const {
    checkSession,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    logout,
    user,
  } = $auth0()

  const permissions = ref<string[]>([])
  const slackAvatar = ref<string>()

  const clear = () =>
    logout({ logoutParams: { returnTo: window.location.origin } })

  watch(
    [isAuthenticated, user],
    async ([authenticated, currentUser]) => {
      if (!authenticated) {
        permissions.value = []
        slackAvatar.value = undefined
        return
      }

      // auth0-vue updates `isAuthenticated` and `user` in separate reactive
      // steps (user is populated after an internal await) — wait for the
      // user to actually be hydrated before gating on their email domain,
      // otherwise this fires on `user === undefined` and spuriously logs
      // the freshly-authenticated user back out.
      if (!currentUser) return

      const email = currentUser.email
      if (!RIALTIC_EMAIL_DOMAINS.some((suffix) => email?.endsWith(suffix))) {
        await clear()
        return
      }

      try {
        const { payload } = decodeJwt(await getAccessTokenSilently())
        permissions.value = payload.permissions ?? []
      } catch {
        permissions.value = []
      }

      try {
        const profile = await $fetch<{ avatar?: string }>(
          `/api/slack/users/${hash(email)}`,
        )
        slackAvatar.value = profile.avatar
      } catch {
        slackAvatar.value = undefined
      }
    },
    { immediate: true },
  )

  return {
    user: computed(() =>
      user.value && slackAvatar.value
        ? { ...user.value, picture: slackAvatar.value }
        : user.value,
    ),
    session: computed<Session | null>(() =>
      isAuthenticated.value ? { permissions: permissions.value } : null,
    ),
    loggedIn: isAuthenticated,
    ready: computed(() => !isLoading.value),
    fetch: () => checkSession(),
    clear,
  }
})
